import React, { useId } from 'react';
import { useTranslation } from 'localization';
import './ApplyToAllShortcutMolecule.scss';

export interface ApplyToAllShortcutOption {
  /** Stable identifier emitted through onApplyToAll. */
  value: string;
  /** Localized, human-readable label — rendered inside the pill and used for the tooltip. */
  label: string;
  /** Full PrimeIcons class string (e.g. "pi pi-check-circle"). */
  icon: string;
  /** Accent color for the pill's icon. CSS color or var() reference. */
  color: string;
  /** Optionally disable a single pill. */
  disabled?: boolean;
}

export interface ApplyToAllShortcutMoleculeProps {
  /** Status options rendered as pills in insertion order. */
  statuses: ApplyToAllShortcutOption[];
  /** Fires with the status value when a pill is clicked. Momentary — no persistent selection. */
  onApplyToAll: (statusValue: string) => void;
  /**
   * Override for the leading label. When omitted, defaults to the localized
   * `taskList.catchUpStrip.applyToAll` key ("Apply to all →").
   */
  label?: string;
  /** Disable every pill. */
  disabled?: boolean;
  className?: string;
}

const ApplyToAllShortcutMolecule: React.FC<ApplyToAllShortcutMoleculeProps> = ({
  statuses,
  onApplyToAll,
  label,
  disabled = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const resolvedLabel = label ?? t('taskList.catchUpStrip.applyToAll');
  const labelId = useId();

  const classes = ['apply-to-all-shortcut-molecule', className]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes}>
      <span id={labelId} className="apply-to-all-shortcut-molecule__label">
        {resolvedLabel}
      </span>
      <div
        className="apply-to-all-shortcut-molecule__pills"
        aria-labelledby={labelId}
      >
        {statuses.map((status) => {
          const optionDisabled = disabled || Boolean(status.disabled);
          return (
            <button
              key={status.value}
              type="button"
              title={status.label}
              disabled={optionDisabled}
              className="apply-to-all-shortcut-molecule__pill"
              onClick={() => {
                if (!optionDisabled) {
                  onApplyToAll(status.value);
                }
              }}
            >
              <i
                className={`apply-to-all-shortcut-molecule__pill-icon ${status.icon}`}
                style={{ color: status.color }}
                aria-hidden="true"
              />
              <span className="apply-to-all-shortcut-molecule__pill-text">
                {status.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

ApplyToAllShortcutMolecule.displayName = 'ApplyToAllShortcutMolecule';

export default ApplyToAllShortcutMolecule;
